import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ContentLayout from '@cloudscape-design/components/content-layout';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Grid from '@cloudscape-design/components/grid';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import Spinner from '@cloudscape-design/components/spinner';
import type { Evaluation, EvaluationCategory } from '../types';
import { getEvaluation } from '../services/evaluation';

/**
 * Props for the CategoryScoreCard component.
 */
interface CategoryScoreCardProps {
  category: EvaluationCategory;
}

/**
 * CategoryScoreCard Component - displays a single category score with progress bar and feedback.
 */
function CategoryScoreCard({ category }: CategoryScoreCardProps): React.ReactElement {
  const percentage = Math.round((category.score / category.maxScore) * 100);

  return (
    <Container
      data-testid={`category-card-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <SpaceBetween size="s">
        <Box variant="h4">{category.name}</Box>
        <ProgressBar
          value={percentage}
          label={`${category.score}/${category.maxScore}`}
          description={`${percentage}%`}
          variant="standalone"
        />
        <Box variant="small" color="text-body-secondary">
          {category.feedback}
        </Box>
        {category.toImprove && (
          <Box variant="small">
            <Box variant="strong" color="text-status-info">💡 To Improve:</Box>
            <Box color="text-body-secondary">{category.toImprove}</Box>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
}

/**
 * Get the score color variant based on the score value.
 */
function getScoreColorClass(score: number): 'text-status-success' | 'text-status-warning' | 'text-status-error' {
  if (score >= 80) return 'text-status-success';
  if (score >= 60) return 'text-status-warning';
  return 'text-status-error';
}

/**
 * Evaluation View Page - Session evaluation results and downloads
 */
export function EvaluationViewPage(): React.ReactElement {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    async function fetchEvaluation() {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      try {
        if (retryCount === 0) {
          setLoading(true);
        }
        setError(null);
        const data = await getEvaluation(sessionId);
        setEvaluation(data);
        setIsProcessing(false);
      } catch (err) {
        console.error('Error fetching evaluation:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load evaluation';
        
        // Check if it's a "not available yet" error - treat as processing
        if (errorMessage.includes('not available') || errorMessage.includes('not found')) {
          setIsProcessing(true);
          setError(null);
        } else {
          setError(errorMessage);
          setIsProcessing(false);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchEvaluation();
  }, [sessionId, retryCount]);

  // Auto-refresh when processing (every 5 seconds, max 24 attempts = 2 minutes)
  useEffect(() => {
    if (isProcessing && retryCount < 24) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isProcessing, retryCount]);

  // Handle download actions - use content if available, otherwise URL
  const handleDownloadTranscript = (): void => {
    if (evaluation?.transcriptContent) {
      // Create blob and download
      const blob = new Blob([evaluation.transcriptContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcript-${sessionId}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (evaluation?.transcriptUrl) {
      window.open(evaluation.transcriptUrl, '_blank');
    }
  };

  const handleDownloadReport = (): void => {
    if (evaluation?.reportContent) {
      // Create blob and download
      const blob = new Blob([evaluation.reportContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `evaluation-report-${sessionId}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else if (evaluation?.evaluationUrl) {
      window.open(evaluation.evaluationUrl, '_blank');
    }
  };

  // Check if downloads are available
  const hasTranscript = !!(evaluation?.transcriptContent || evaluation?.transcriptUrl);
  const hasReport = !!(evaluation?.reportContent || evaluation?.evaluationUrl);

  // Loading state
  if (loading) {
    return (
      <ContentLayout
        data-testid="evaluation-view-page"
        header={
          <Header variant="h1" description={sessionId ? `Session ID: ${sessionId}` : ''}>
            Evaluation Results
          </Header>
        }
      >
        <Container>
          <Box textAlign="center" padding={{ vertical: 'xxl' }}>
            <Spinner size="large" />
            <Box variant="p" padding={{ top: 's' }}>
              Loading evaluation...
            </Box>
          </Box>
        </Container>
      </ContentLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <ContentLayout
        data-testid="evaluation-view-page"
        header={
          <Header variant="h1" description={sessionId ? `Session ID: ${sessionId}` : ''}>
            Evaluation Results
          </Header>
        }
      >
        <Alert type="error" header="Error Loading Evaluation">
          {error}
        </Alert>
      </ContentLayout>
    );
  }

  // Processing state (evaluation being generated)
  if (isProcessing) {
    return (
      <ContentLayout
        data-testid="evaluation-view-page"
        header={
          <Header
            variant="h1"
            description={sessionId ? `Session ID: ${sessionId}` : ''}
          >
            Evaluation Results
          </Header>
        }
      >
        <Container>
          <Box textAlign="center" padding={{ vertical: 'xxl' }}>
            <Box variant="h3" padding={{ top: 'm' }}>
              Your Evaluation is Being Processed
            </Box>
            <Box variant="p" color="text-body-secondary" padding={{ top: 's' }}>
              Your session evaluation typically takes 1-2 minutes to complete.
              You can check your results from the Dashboard.
            </Box>
            <Box padding={{ top: 'l' }}>
              <Button variant="primary" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </Box>
          </Box>
        </Container>
      </ContentLayout>
    );
  }

  // Not found state (no session ID provided)
  if (!evaluation) {
    return (
      <ContentLayout
        data-testid="evaluation-view-page"
        header={
          <Header
            variant="h1"
            description={sessionId ? `Session ID: ${sessionId}` : 'No session specified'}
          >
            Evaluation Results
          </Header>
        }
      >
        <Alert
          data-testid="evaluation-not-found-alert"
          type="info"
          header="No Session Selected"
        >
          Please navigate to this page from your session history on the Dashboard.
        </Alert>
      </ContentLayout>
    );
  }

  // Success state - show evaluation
  return (
    <ContentLayout
      data-testid="evaluation-view-page"
      header={
        <Header
          variant="h1"
          description={`Evaluation results for session: ${sessionId}`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                data-testid="download-transcript-button"
                iconName="download"
                onClick={handleDownloadTranscript}
                disabled={!hasTranscript}
              >
                Download Transcript
              </Button>
              <Button
                data-testid="download-report-button"
                iconName="download"
                variant="primary"
                onClick={handleDownloadReport}
                disabled={!hasReport}
              >
                Download Report
              </Button>
            </SpaceBetween>
          }
        >
          Evaluation Results
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Overall Score Section */}
        <Container
          data-testid="overall-score-container"
          header={<Header variant="h2">Overall Score</Header>}
        >
          <Box textAlign="center" padding={{ vertical: 'l' }}>
            <Box
              data-testid="overall-score-value"
              variant="h1"
              fontSize="display-l"
              fontWeight="bold"
              color={getScoreColorClass(evaluation.overallScore)}
            >
              {evaluation.overallScore}
            </Box>
            <Box variant="p" color="text-body-secondary">
              out of 100
            </Box>
          </Box>
        </Container>

        {/* Category Scores Section */}
        {evaluation.categories && evaluation.categories.length > 0 && (
          <Container
            data-testid="category-scores-container"
            header={<Header variant="h2">Category Scores</Header>}
          >
            <Grid
              gridDefinition={evaluation.categories.map(() => ({ colspan: { default: 12, s: 6 } }))}
            >
              {evaluation.categories.map((category) => (
                <CategoryScoreCard key={category.name} category={category} />
              ))}
            </Grid>
          </Container>
        )}

        {/* Feedback Section */}
        <Container
          data-testid="feedback-container"
          header={<Header variant="h2">Feedback</Header>}
        >
          <Box
            data-testid="feedback-text"
            variant="p"
            padding={{ vertical: 's' }}
          >
            {evaluation.feedback || 'No feedback available.'}
          </Box>
        </Container>

        {/* Downloads Section */}
        <Container
          data-testid="downloads-container"
          header={<Header variant="h2">Downloads</Header>}
        >
          <SpaceBetween size="m">
            <Box>
              <Box variant="awsui-key-label">Session Transcript</Box>
              <Button
                data-testid="download-transcript-link"
                variant="link"
                iconName="download"
                onClick={handleDownloadTranscript}
                disabled={!hasTranscript}
              >
                {hasTranscript ? 'Download transcript file' : 'Transcript not available'}
              </Button>
            </Box>
            <Box>
              <Box variant="awsui-key-label">Evaluation Report</Box>
              <Button
                data-testid="download-report-link"
                variant="link"
                iconName="download"
                onClick={handleDownloadReport}
                disabled={!hasReport}
              >
                {hasReport ? 'Download evaluation report' : 'Report not available'}
              </Button>
            </Box>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
}

export default EvaluationViewPage;
